import React from 'react';
import { TableHeader, TableRow, TableHead } from './Table';
import { BodyS } from '../typography';
import { TableSortableHeaderLabel } from './TableSortableHeaderLabel';
import type { ITableSectionColumn, ITableSortState } from './types';
import { ACTIONS_COLUMN_ID, CHECKBOX_COLUMN_WIDTH_PX, OrderBy } from './types';

function headerColumnAriaSort(
	isSortableDataColumn: boolean,
	sort: ITableSortState | undefined,
	sortFieldKey: string
): undefined | 'none' | 'ascending' | 'descending' {
	if (!isSortableDataColumn || sort === undefined) {
		return undefined;
	}
	if (sort.field !== sortFieldKey) {
		return 'none';
	}
	if (sort.order === OrderBy.DESC) {
		return 'descending';
	}
	return 'ascending';
}

export interface ITableHeaderSectionProps {
	columns: ITableSectionColumn[];
	checkboxColumn?: React.ReactNode;
	hasToolbar?: boolean;
	isSortable?: boolean;
	sort?: ITableSortState;
	onSortChange?: (next: ITableSortState) => void;
}

function TableHeaderSection({
	columns,
	checkboxColumn,
	hasToolbar,
	isSortable = false,
	sort,
	onSortChange,
}: Readonly<ITableHeaderSectionProps>) {
	const sortEnabled = Boolean(isSortable && sort && onSortChange);
	const hasCheckboxColumn = Boolean(checkboxColumn);

	return (
		<TableHeader hasToolbar={hasToolbar}>
			<TableRow isHoverDisabled>
				{hasCheckboxColumn && (
					<TableHead align="center" className="w-12 pr-0" sticky="left">
						{checkboxColumn}
					</TableHead>
				)}
				{columns.map((column) => {
					const cellAlign: 'left' | 'center' | 'right' =
						column.align ??
						(column.id === ACTIONS_COLUMN_ID ? 'right' : 'left');

					const stickyLeftOffset =
						column.sticky === 'left' && hasCheckboxColumn
							? CHECKBOX_COLUMN_WIDTH_PX
							: undefined;

					const sortFieldKey = column.field || column.id;
					const isSortableDataColumn =
						sortEnabled &&
						!!column.isSortable &&
						column.id !== ACTIONS_COLUMN_ID &&
						!!column.name.length;

					const ariaSort = headerColumnAriaSort(
						isSortableDataColumn,
						sort,
						sortFieldKey
					);

					return (
						<TableHead
							key={column.id}
							align={cellAlign}
							sticky={column.sticky}
							stickyLeftOffset={stickyLeftOffset}
							aria-sort={ariaSort}
						>
							{isSortableDataColumn ? (
								<TableSortableHeaderLabel
									label={column.name}
									sortField={sortFieldKey}
									sort={sort!}
									onSort={() => {
										if (sort!.field === sortFieldKey) {
											let nextOrder = OrderBy.DESC;
											if (sort!.order === OrderBy.DESC) {
												nextOrder = OrderBy.ASC;
											}
											onSortChange!({
												field: sortFieldKey,
												order: nextOrder,
											});
										} else {
											onSortChange!({
												field: sortFieldKey,
												order: OrderBy.ASC,
											});
										}
									}}
								/>
							) : (
								<BodyS color="primary">{column.name}</BodyS>
							)}
						</TableHead>
					);
				})}
			</TableRow>
		</TableHeader>
	);
}

export { TableHeaderSection };
